/**
 * iter84.56.8 — Shared helpers for audit cards.
 * iter84.56.9-hotfix — `_StatusPill` + `_scrollToAnchor` migrated here
 * from `OrphanPicksAuditCard.jsx` (where they were defined but unused
 * after the split — `ParitySweepCard.jsx` was the actual consumer and
 * was throwing `ReferenceError: _StatusPill is not defined` at render).
 *
 * VERDICT_STYLE: visual style by verdict. VerdictIcon: icon by verdict.
 * StatusPill: pill renderer for parity-sweep row statuses.
 * scrollToAnchor: smooth-scroll + flash helper for the "Open card →"
 * links inside the parity sweep card.
 */
import {
    CheckCircle2,
    AlertTriangle,
    XCircle,
    FileText,
    ClipboardCheck,
} from "lucide-react";


export const VERDICT_STYLE = {
    clear:   { pill: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", label: "attested" },
    warn:    { pill: "bg-amber-500/15 text-amber-300 border-amber-500/40", label: "warn" },
    fail:    { pill: "bg-rose-500/15 text-rose-400 border-rose-500/40", label: "fail" },
    missing: { pill: "bg-zinc-700/30 text-zinc-400 border-zinc-700", label: "not attested" },
    error:   { pill: "bg-rose-500/15 text-rose-400 border-rose-500/40", label: "error" },
};

export function VerdictIcon({ verdict }) {
    if (verdict === "clear") return <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />;
    if (verdict === "warn") return <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />;
    if (verdict === "fail" || verdict === "error") return <XCircle className="w-4 h-4 text-rose-400 shrink-0" />;
    return <FileText className="w-4 h-4 text-zinc-500 shrink-0" />;
}

// Re-exported for ParitySweepCard. Renamed to drop the leading underscore
// (the underscore was a "file-private" convention in the original
// monolithic ReleaseOpsAuditCards.jsx; it's pointless once these are
// part of the shared module).
export function scrollToAnchor(testId) {
    if (!testId) return;
    const el = document.querySelector(`[data-testid="${testId}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    // Quick attention flash so the operator sees where the sweep pointed.
    el.classList.add("ring-2", "ring-cyber", "ring-offset-2", "ring-offset-zinc-950");
    setTimeout(() => {
        el.classList.remove("ring-2", "ring-cyber", "ring-offset-2", "ring-offset-zinc-950");
    }, 1800);
}

export function StatusPill({ status }) {
    const cls = {
        pass: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
        warn: "bg-amber-500/15 text-amber-300 border-amber-500/40",
        fail: "bg-rose-500/15 text-rose-300 border-rose-500/40",
        ack: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40",
    }[status] || "bg-zinc-500/15 text-zinc-300 border-zinc-500/40";
    const Icon = status === "pass"
        ? CheckCircle2
        : status === "fail"
            ? XCircle
            : status === "ack"
                ? ClipboardCheck
                : AlertTriangle;
    return (
        <span
            data-testid={`parity-sweep-status-${status}`}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 border ${cls} text-[9px] font-bold uppercase tracking-widest rounded-sm shrink-0`}
        >
            <Icon className="w-3 h-3" />
            {status === "ack" ? "ACK'd" : status}
        </span>
    );
}
